import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

function formatCurrency(n) {
  return "Rs." + (Number(n) || 0).toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function numberToWords(num) {
  const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten",
    "Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  function convert(n) {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n/10)] + (n%10 ? " " + ones[n%10] : "");
    if (n < 1000) return ones[Math.floor(n/100)] + " Hundred" + (n%100 ? " " + convert(n%100) : "");
    if (n < 100000) return convert(Math.floor(n/1000)) + " Thousand" + (n%1000 ? " " + convert(n%1000) : "");
    if (n < 10000000) return convert(Math.floor(n/100000)) + " Lakh" + (n%100000 ? " " + convert(n%100000) : "");
    return convert(Math.floor(n/10000000)) + " Crore" + (n%10000000 ? " " + convert(n%10000000) : "");
  }
  const rupees = Math.floor(num);
  if (rupees === 0) return "Zero Rupees Only";
  return convert(rupees) + " Rupees Only";
}

async function fetchAsDataUrl(path) {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateSalarySlipDoc(employee) {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 54; // 0.75in

  // Header: try real logo, else bold text fallback
  const logoDataUrl = await fetchAsDataUrl("/IWS-01.png");
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", (pageWidth - 260) / 2, 30, 260, 40);
    } catch {
      doc.setFontSize(16); doc.setFont("helvetica", "bold");
      doc.text("INVINCIBLE WEB SOLUTIONS", pageWidth / 2, 50, { align: "center" });
    }
  } else {
    doc.setFontSize(16); doc.setFont("helvetica", "bold");
    doc.text("INVINCIBLE WEB SOLUTIONS", pageWidth / 2, 50, { align: "center" });
    doc.setFontSize(9); doc.setFont("helvetica", "normal");
    doc.text("A Digital Advertising Agency", pageWidth / 2, 65, { align: "center" });
  }

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(`Salary Slip For The Month of ${employee.salaryMonth}`, pageWidth / 2, 95, { align: "center" });

  autoTable(doc, {
    startY: 115,
    margin: { left: margin, right: margin },
    body: [
      ["Employee ID:", employee.empId],
      ["Name:", employee.name],
      ["Designation:", employee.designation || "-"],
      ["Department:", employee.department || "-"],
      ["Date Of Joining:", employee.dateOfJoining || "N/A"],
    ],
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 6 },
    columnStyles: {
      0: { cellWidth: 160, fontStyle: "bold" },
      1: { cellWidth: "auto" },
    },
  });

  const y1 = doc.lastAutoTable.finalY + 20;
  autoTable(doc, {
    startY: y1,
    margin: { left: margin, right: margin },
    head: [["Earnings", "Amount"]],
    body: [
      ["Basic Salary:", formatCurrency(employee.basic)],
      ["Allowances:", formatCurrency(employee.allowances)],
      ...(employee.bonus ? [["Bonus:", formatCurrency(employee.bonus)]] : []),
    ],
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 6 },
    headStyles: { fillColor: [230, 230, 230], textColor: [0,0,0], fontStyle: "bold" },
    columnStyles: { 1: { halign: "right" } },
  });

  const y2 = doc.lastAutoTable.finalY;
  autoTable(doc, {
    startY: y2,
    margin: { left: margin, right: margin },
    head: [["Deductions", "Amount"]],
    body: [["Deduction:", formatCurrency(employee.deductions)]],
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 6 },
    headStyles: { fillColor: [230, 230, 230], textColor: [0,0,0], fontStyle: "bold" },
    columnStyles: { 1: { halign: "right" } },
  });

  const y3 = doc.lastAutoTable.finalY;
  autoTable(doc, {
    startY: y3,
    margin: { left: margin, right: margin },
    body: [[
      { content: "Net Salary Amount:", styles: { fontStyle: "bold" } },
      { content: formatCurrency(employee.netPay), styles: { fontStyle: "bold", halign: "right" } },
    ]],
    theme: "grid",
    styles: { fontSize: 11, cellPadding: 6, fillColor: [245, 245, 245] },
  });

  const y4 = doc.lastAutoTable.finalY + 40;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Amount in Words:", margin, y4);
  doc.setFont("helvetica", "normal");
  doc.text(numberToWords(employee.netPay), margin + 100, y4);

  // Footer: Prepared By (left) / Digital signature + Approved By (right)
  const footerY = doc.internal.pageSize.getHeight() - 130;
  const sigDataUrl = await fetchAsDataUrl("/digital_signature.png");
  if (sigDataUrl) {
    try {
      doc.addImage(sigDataUrl, "PNG", pageWidth - margin - 130, footerY, 130, 65);
    } catch { /* ignore */ }
  }

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("Prepared By:", margin, footerY + 90);
  doc.text(employee.preparedBy || "-", margin, footerY + 105);

  if (employee.approvedBy) {
    doc.text(`Digitally Signed By: ${employee.approvedBy}`, pageWidth - margin - 190, footerY + 90);
  }
  doc.text(`Date: ${employee.slipDate || "-"}`, pageWidth - margin - 190, footerY + 105);

  return doc;
}
